import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { PauseCircleMajor, PlayCircleMajor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch, useSelector } from 'react-redux'
import { getPartnerId } from 'src/selectors/user'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { TeamMembersTableContext } from './TeamMembersTable.context'
import { DeactivateTeamMember, ActivateTeamMember } from 'src/constants/actionTypes'

const TeamMemberActionMenuComponent = () => {
  const dispatch = useDispatch()
  const partnerId = useSelector(getPartnerId)

  const {
    teamMemberToAction,
    actionAnchor,
    closeTeamMemberItemAction,
    refreshTeamMembers,
    clearTeamMemberToAction,
  } = useContext(TeamMembersTableContext)

  const isOpen = Boolean(actionAnchor)

  const [isActivateConfirmOpen, setIsActivateConfirmOpen] = useState(false)
  const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false)

  const handleOpenActivateConfirm = useCallback(() => {
    setIsActivateConfirmOpen(true)
  }, [])

  const handleCloseActivateConfirm = useCallback(() => {
    setIsActivateConfirmOpen(false)
  }, [])

  const handleOpenDeactivateConfirm = useCallback(() => {
    setIsDeactivateConfirmOpen(true)
  }, [])

  const handleCloseDeactivateConfirm = useCallback(() => {
    setIsDeactivateConfirmOpen(false)
  }, [])

  const handleActivateSubmit = useCallback(async () => {
    if (!teamMemberToAction) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        ActivateTeamMember.request({
          partnerId,
          teamMemberId: teamMemberToAction.id,
        }),
      )

      showToast('User activated!', { variant: 'success' })

      clearTeamMemberToAction()
      refreshTeamMembers()
    } catch (err) {
      handleApiError(err)
    } finally {
      handleCloseActivateConfirm()
    }
  }, [teamMemberToAction])

  const handleDeactivateSubmit = useCallback(async () => {
    if (!teamMemberToAction) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeactivateTeamMember.request({
          partnerId,
          teamMemberId: teamMemberToAction.id,
        }),
      )

      showToast('User deactivated!', { variant: 'success' })

      clearTeamMemberToAction()
      refreshTeamMembers()
    } catch (err) {
      handleApiError(err)
    } finally {
      handleCloseDeactivateConfirm()
    }
  }, [teamMemberToAction])

  const actionItems: MenuActionItem[] = useMemo(() => {
    if (!teamMemberToAction) {
      return []
    }

    const res: MenuActionItem[] = []

    if (!teamMemberToAction.disabled) {
      res.push({
        label: 'Deactivate user',
        icon: <PauseCircleMajor width={20} height={20} />,
        onClick: () => {
          closeTeamMemberItemAction()
          handleOpenDeactivateConfirm()
        },
      })
    }

    if (teamMemberToAction.disabled) {
      res.push({
        label: 'Activate user',
        icon: <PlayCircleMajor width={20} height={20} />,
        onClick: () => {
          closeTeamMemberItemAction()
          handleOpenActivateConfirm()
        },
      })
    }

    return res
  }, [teamMemberToAction])

  return (
    <>
      <ActionMenu
        anchorEl={actionAnchor}
        itemClassName={tw`min-w-[208px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={closeTeamMemberItemAction}
      />
      <SensitiveActionModal
        isOpen={isActivateConfirmOpen}
        title="Activate user?"
        text="Are you sure you want to activate user?"
        color="green"
        submitLabel="Activate"
        onClose={handleCloseActivateConfirm}
        onSubmit={handleActivateSubmit}
      />
      <SensitiveActionModal
        isOpen={isDeactivateConfirmOpen}
        title="Deactivate user?"
        text="Are you sure you want to deactivate user?"
        color="red"
        submitLabel="Deactivate"
        onClose={handleCloseDeactivateConfirm}
        onSubmit={handleDeactivateSubmit}
      />
    </>
  )
}

export const TeamMemberActionMenu = memo(TeamMemberActionMenuComponent)
